import {
  Button,
  Calendar,
  CreditCard,
  DollarSign,
  Eye,
  FileText,
  Input,
  Search,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/molecules/Modal/Modal";
import { invoiceApi } from "../../services/invoice.api";
import {
  InvoiceManagerResponse,
  InvoiceResponse,
  InvoiceStatsResponse,
  InvoiceType,
  PagedResponse,
} from "../../types/invoice.definition";
import { formatMoneyOrNull } from "../../utils/money-utils";
import {
  formatDateTime,
  formatDateTimeOrNull,
  formatMonthYear,
} from "../../utils/date-format-utils";
import { licenceValueLabel } from "../../utils/enum-label-utils";
import { scrollToTop } from "../../utils/scroll-utils";

const PAGE_SIZE = 10;

const TABS: { type: InvoiceType; labelKey: string }[] = [
  { type: "FACILITY", labelKey: "admin.invoices.tabs.facility" },
  { type: "DOCTOR", labelKey: "admin.invoices.tabs.doctor" },
];

const SECTION_TITLE_CLASS =
  "mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700";

interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
}

const DetailSection: React.FC<DetailSectionProps> = ({ title, children }) => (
  <section>
    <h4 className={SECTION_TITLE_CLASS}>{title}</h4>
    <div className="grid grid-cols-2 gap-4">{children}</div>
  </section>
);

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, children }) => (
  <div>
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const orDash = (value: string | number | null | undefined) => value ?? "—";

/** An amount in the UI's language, or "—" when the API did not send one. */
const amountLabel = (value: number | null | undefined) =>
  formatMoneyOrNull(value) ?? "—";

/**
 * MED-1583: the invoice amount, preceded — when the API reports a rebate
 * promotion on this invoice — by the amount before the promotion struck
 * through and the promotion that discounted it. All three values come from
 * the API; nothing is recomputed here.
 */
const InvoiceAmount: React.FC<{ invoice: InvoiceResponse }> = ({ invoice }) => {
  const { t } = useTranslation();
  if (invoice.baseAmount == null || invoice.rebatePercentage == null) {
    return <>{amountLabel(invoice.amount)}</>;
  }
  return (
    <span className="inline-flex flex-col">
      <span
        className="text-xs text-gray-500 line-through"
        data-testid="invoice-base-amount"
      >
        {amountLabel(invoice.baseAmount)}
      </span>
      <span className="text-xs font-medium text-success-700">
        {invoice.promotionCode
          ? t("admin.invoices.promotionApplied", {
              code: invoice.promotionCode,
              percent: invoice.rebatePercentage,
            })
          : t("admin.invoices.promotionAppliedNoCode", {
              percent: invoice.rebatePercentage,
            })}
      </span>
      <span>{amountLabel(invoice.amount)}</span>
    </span>
  );
};

/** The invoice's creation date for the row subtitle, or null when unusable. */
const createdAtLabel = (invoice: InvoiceResponse) =>
  formatDateTimeOrNull(invoice.createdAt);

/** Every manager of a facility invoice; falls back to `managerName` for an API without `managers`. */
const managersOf = (invoice: InvoiceResponse): InvoiceManagerResponse[] =>
  invoice.managers ??
  (invoice.managerName
    ? [{ name: invoice.managerName, email: null, phone: null }]
    : []);

export const InvoiceManagement: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<InvoiceType>("FACILITY");
  const [page, setPage] = useState<PagedResponse<InvoiceResponse> | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceResponse | null>(null);
  const [stats, setStats] = useState<InvoiceStatsResponse | null>(null);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const [statsMonth, setStatsMonth] = useState(currentMonth);
  const [dueMonthInput, setDueMonthInput] = useState(currentMonth);
  const [dueMonthFilter, setDueMonthFilter] = useState<string | null>(null);

  // Each tab lists one invoice type; a response for a tab (or page) already left is dropped.
  useEffect(() => {
    let cancelled = false;
    const loadInvoices = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        let data: PagedResponse<InvoiceResponse>;
        if (dueMonthFilter) {
          const [year, month] = dueMonthFilter.split("-").map(Number);
          data = await invoiceApi.getDue(
            year,
            month,
            currentPage,
            PAGE_SIZE,
            activeTab
          );
        } else {
          data = await invoiceApi.getAll(
            currentPage,
            PAGE_SIZE,
            undefined,
            activeTab
          );
        }
        if (!cancelled) {
          setPage(data);
        }
      } catch {
        if (!cancelled) {
          setLoadError(t("admin.invoices.loadError"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    loadInvoices();
    return () => {
      cancelled = true;
    };
  }, [t, currentPage, dueMonthFilter, activeTab]);

  useEffect(() => {
    let cancelled = false;
    const [year, month] = statsMonth.split("-").map(Number);
    invoiceApi
      .getStats(year, month, activeTab)
      .then(data => {
        if (!cancelled) {
          setStats(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [statsMonth, activeTab]);

  const isDoctorTab = activeTab === "DOCTOR";
  const invoices = page?.content ?? [];

  const term = searchTerm.toLowerCase();
  const matches = (value: string | null | undefined) =>
    (value ?? "").toLowerCase().includes(term);
  const filteredInvoices = invoices.filter(
    invoice =>
      String(invoice.id).includes(searchTerm) ||
      (isDoctorTab
        ? matches(invoice.doctorName) || matches(invoice.doctorEmail)
        : matches(invoice.facilityName) ||
          managersOf(invoice).some(manager => matches(manager.name)))
  );

  const handleTabChange = (tab: InvoiceType) => {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    setCurrentPage(0);
    setPage(null);
    setSearchTerm("");
    setShowInvoiceModal(false);
  };

  const handleViewInvoice = (invoice: InvoiceResponse) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "SENT":
        return "bg-blue-100 text-blue-800";
      case "PAID":
        return "bg-green-100 text-green-800";
      case "OVERDUE":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return t("admin.invoices.status.pending");
      case "SENT":
        return t("admin.invoices.status.sent");
      case "PAID":
        return t("admin.invoices.status.paid");
      case "OVERDUE":
        return t("admin.invoices.status.overdue");
      default:
        return status;
    }
  };

  /** Label of a licence enum value (plan, contract duration, source...), or the raw value if unknown. */
  const valueLabel = (value: string | null | undefined) =>
    licenceValueLabel(t, value) || "—";

  const totalElements = page?.totalElements ?? 0;
  const totalPages = page?.totalPages ?? 0;
  const statsDisplayMonth = stats?.billingPeriod ?? statsMonth;
  const statsMonthLabel = formatMonthYear(statsDisplayMonth);

  const selectedIsDoctor =
    (selectedInvoice?.invoiceType ?? activeTab) === "DOCTOR";
  const selectedManagers = selectedInvoice ? managersOf(selectedInvoice) : [];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.invoices.title")}
          </h2>
          <p className="text-gray-600">{t("admin.invoices.subtitle")}</p>
        </div>
      </div>

      {/* Tab bar: one invoice type per tab */}
      <div className="mb-6 flex border-b border-gray-200" role="tablist">
        {TABS.map(tab => (
          <button
            key={tab.type}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.type}
            onClick={() => handleTabChange(tab.type)}
            className={`border-b-2 px-5 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.type
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="page-section grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="card-container border-l-4 border-ews-primary">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t("admin.invoices.totalAmount")}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {amountLabel(stats?.totalAmount ?? 0)}
              </p>
              <p className="mt-1 text-xs text-gray-400">{statsMonthLabel}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card-container border-l-4 border-warning-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t("admin.invoices.pendingAmount")}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {amountLabel(stats?.pendingAmount ?? 0)}
              </p>
              <p className="mt-1 text-xs text-gray-400">{statsMonthLabel}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <CreditCard className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card-container border-l-4 border-success-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                {t("admin.invoices.totalInvoices")}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.totalInvoices ?? 0}
              </p>
              <p className="mt-1 text-xs text-gray-400">{statsMonthLabel}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search + Due Month Filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Input
            placeholder={t("admin.invoices.searchPlaceholder")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              {t("admin.invoices.dueFilter")}
            </label>
            <input
              type="month"
              value={dueMonthInput}
              onChange={e => setDueMonthInput(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button
            onClick={() => {
              setDueMonthFilter(dueMonthInput);
              setCurrentPage(0);
              setStatsMonth(dueMonthInput);
            }}
          >
            {t("admin.invoices.viewDue")}
          </Button>
          {dueMonthFilter && (
            <Button
              variant="outline"
              onClick={() => {
                setDueMonthFilter(null);
                setCurrentPage(0);
              }}
            >
              {t("admin.invoices.clearDueFilter")}
            </Button>
          )}
        </div>
      </div>

      {/* Active due filter badge */}
      {dueMonthFilter && (
        <p className="text-sm font-medium text-blue-600">
          {t("admin.invoices.dueFilterActive", {
            month: formatMonthYear(dueMonthFilter),
          })}
        </p>
      )}

      {/* Error / Loading */}
      {loadError && (
        <div className="rounded border border-red-400 bg-red-100 p-3 text-red-700">
          {loadError}
        </div>
      )}

      {/* Invoices Table */}
      <div className="page-section card-container overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">
              {t("common.loading")}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("admin.invoices.table.invoice")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {isDoctorTab
                      ? t("admin.invoices.table.doctor")
                      : t("admin.invoices.table.facility")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("admin.invoices.billingPeriod")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("admin.invoices.table.amount")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {isDoctorTab
                      ? t("admin.invoices.details.plan")
                      : t("admin.invoices.licenseType")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("admin.invoices.table.dueDate")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("admin.invoices.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      {t("admin.invoices.noInvoices")}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map(invoice => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          #{invoice.id}
                        </div>
                        {/* MED-1513: `createdAt` is an ISO-8601 instant, not
                            epoch seconds — running it through
                            `Number(...) * 1000` made every row read
                            "Invalid Date". The subtitle is dropped entirely
                            when the API sends no usable date. */}
                        {createdAtLabel(invoice) && (
                          <div className="text-sm text-gray-500">
                            {createdAtLabel(invoice)}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {orDash(
                            isDoctorTab
                              ? invoice.doctorName
                              : invoice.facilityName
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {orDash(
                            isDoctorTab
                              ? invoice.doctorEmail
                              : invoice.managerName
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {invoice.billingPeriod}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        <InvoiceAmount invoice={invoice} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {valueLabel(invoice.licenceType)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {invoice.dueDate ? (
                          <div className="flex items-center">
                            <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                            {formatDateTime(invoice.dueDate)}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(invoice.status)}`}
                        >
                          {getStatusText(invoice.status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center text-sm font-medium">
                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className="text-blue-400 hover:text-blue-600"
                          title={t("admin.invoices.view")}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {t("pagination.showing", {
              start: currentPage * PAGE_SIZE + 1,
              end: Math.min((currentPage + 1) * PAGE_SIZE, totalElements),
              total: totalElements,
            })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={e => {
                setCurrentPage(p => p - 1);
                scrollToTop(e.currentTarget);
              }}
              disabled={page?.first ?? true}
            >
              {t("pagination.previous")}
            </Button>
            <span className="text-sm text-gray-600">
              {t("pagination.page")} {currentPage + 1} {t("pagination.of")}{" "}
              {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={e => {
                setCurrentPage(p => p + 1);
                scrollToTop(e.currentTarget);
              }}
              disabled={page?.last ?? true}
            >
              {t("pagination.next")}
            </Button>
          </div>
        </div>
      )}

      {/* Invoice Details Modal: full details of the facility or doctor invoice */}
      {showInvoiceModal && selectedInvoice && (
        <Modal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          title={`${t("admin.invoices.invoice")} #${selectedInvoice.id}`}
          size="lg"
        >
          <div className="space-y-6 p-6">
            <DetailSection title={t("admin.invoices.details.invoice")}>
              <DetailField label={t("admin.invoices.billingPeriod")}>
                {selectedInvoice.billingPeriod}
              </DetailField>
              <DetailField label={t("admin.invoices.amount")}>
                <InvoiceAmount invoice={selectedInvoice} />
              </DetailField>
              <DetailField label={t("admin.invoices.details.periodMonths")}>
                {orDash(selectedInvoice.periodMonths)}
              </DetailField>
              <DetailField label={t("admin.invoices.dueDate")}>
                {formatDateTime(selectedInvoice.dueDate)}
              </DetailField>
              <DetailField label={t("admin.invoices.table.status")}>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(selectedInvoice.status)}`}
                >
                  {getStatusText(selectedInvoice.status)}
                </span>
              </DetailField>
              {selectedInvoice.paidDate && (
                <DetailField label={t("admin.invoices.paidDate")}>
                  {formatDateTime(selectedInvoice.paidDate)}
                </DetailField>
              )}
            </DetailSection>

            {selectedIsDoctor ? (
              <>
                <DetailSection title={t("admin.invoices.details.doctor")}>
                  <DetailField label={t("admin.invoices.details.name")}>
                    {orDash(selectedInvoice.doctorName)}
                  </DetailField>
                  <DetailField label={t("admin.invoices.details.email")}>
                    {orDash(selectedInvoice.doctorEmail)}
                  </DetailField>
                  <DetailField label={t("admin.invoices.details.phone")}>
                    {orDash(selectedInvoice.doctorPhone)}
                  </DetailField>
                </DetailSection>

                <DetailSection title={t("admin.invoices.details.licence")}>
                  <DetailField label={t("admin.invoices.details.plan")}>
                    {valueLabel(selectedInvoice.licenceType)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.contractDuration")}
                  >
                    {valueLabel(selectedInvoice.contractDuration)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.licenceSource")}
                  >
                    {valueLabel(selectedInvoice.licenceSource)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.monthlyAmount")}
                  >
                    {amountLabel(selectedInvoice.monthlyAmount)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.licenceStatus")}
                  >
                    {valueLabel(selectedInvoice.licenceStatus)}
                  </DetailField>
                </DetailSection>
              </>
            ) : (
              <>
                <DetailSection title={t("admin.invoices.details.facility")}>
                  <DetailField label={t("admin.invoices.details.name")}>
                    {orDash(selectedInvoice.facilityName)}
                  </DetailField>
                  <DetailField label={t("admin.invoices.details.phone")}>
                    {orDash(selectedInvoice.facilityPhone)}
                  </DetailField>
                  <DetailField label={t("admin.invoices.details.contactEmail")}>
                    {orDash(selectedInvoice.contactEmail)}
                  </DetailField>
                </DetailSection>

                <section>
                  <h4 className={SECTION_TITLE_CLASS}>
                    {t("admin.invoices.details.managers")}
                  </h4>
                  {selectedManagers.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t("admin.invoices.details.noManagers")}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {selectedManagers.map(manager => (
                        <li
                          key={`${manager.name ?? ""}|${manager.email ?? ""}|${manager.phone ?? ""}`}
                          className="rounded-md border border-gray-200 p-3"
                        >
                          <p className="text-sm font-medium text-gray-900">
                            {orDash(manager.name)}
                          </p>
                          {manager.email && (
                            <p className="text-sm text-gray-500">
                              {manager.email}
                            </p>
                          )}
                          {manager.phone && (
                            <p className="text-sm text-gray-500">
                              {manager.phone}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <DetailSection title={t("admin.invoices.details.licence")}>
                  <DetailField label={t("admin.invoices.licenseType")}>
                    {valueLabel(selectedInvoice.licenceType)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.numberOfDoctors")}
                  >
                    {orDash(selectedInvoice.numberOfDoctors)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.contractDuration")}
                  >
                    {valueLabel(selectedInvoice.contractDuration)}
                  </DetailField>
                  <DetailField label={t("admin.invoices.details.billingMode")}>
                    {valueLabel(selectedInvoice.paymentBillingMode)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.monthlyAmount")}
                  >
                    {amountLabel(selectedInvoice.monthlyAmount)}
                  </DetailField>
                  <DetailField
                    label={t("admin.invoices.details.licenceStatus")}
                  >
                    {valueLabel(selectedInvoice.licenceStatus)}
                  </DetailField>
                </DetailSection>
              </>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowInvoiceModal(false)}
              >
                {t("common.close")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
